/* Ordinary collections use cloud-maintained Shopify order, with no index/card fetching.
 * Keep the protected showcase's collection-page sorter: its stored order belongs to the homepage.
 */
(() => {
  const showcase = /\/collections\/vintage-bustier-corset-collection\/?$/.test(location.pathname);
  if (showcase) return;
  const url = new URL(location.href);
  if (url.searchParams.get('view') === 'new-items' && /\/collections\/all\/?$/.test(url.pathname)) {
    url.pathname = url.pathname.replace(/\/all\/?$/, '/new-items');
    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');
    location.replace(url.href);
    return;
  }
  if (url.searchParams.has('sort_by') && url.searchParams.get('sort_by') !== 'manual') {
    url.searchParams.set('sort_by', 'manual');
    location.replace(url.href);
  }
})();
if (/\/collections\/vintage-bustier-corset-collection\/?$/.test(location.pathname)) {
/* Permanent policy: NEW > NEW SALE > regular > regular SALE > NEW SOLD > SOLD.
 * Index ALL matching products before pagination. Runs on Shopify without the
 * merchant's Mac, including after inventory, prices or the 7-day window change.
 */
(() => {
  const NEW_SECONDS = 7 * 24 * 60 * 60;
  function group(product, now) {
    const age = now - Number(product.published);
    const recent = Number(product.published) > 0 && age >= 0 && age < NEW_SECONDS;
    if (!product.available) return recent ? 4 : 5;
    if (recent) return product.sale ? 1 : 0;
    return product.sale ? 3 : 2;
  }
  function order(products, now) {
    return [...products].sort((a, b) => group(a, now) - group(b, now)
      || Number(b.published || b.created) - Number(a.published || a.created)
      || String(a.id).localeCompare(String(b.id)));
  }
  if (typeof module !== 'undefined') module.exports = { group, order };
  if (typeof customElements === 'undefined' || customElements.get('euphoria-product-order')) return;

  async function pooled(values, fn, concurrency = 3) {
    let cursor = 0;
    const output = new Array(values.length);
    await Promise.all(Array.from({ length: Math.min(concurrency, values.length) }, async () => {
      while (cursor < values.length) {
        const i = cursor++;
        output[i] = await fn(values[i]);
      }
    }));
    return output;
  }

  class ProductOrder extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('#ProductGridContainer');
      this.container.removeAttribute('data-euphoria-ready');
      this.controller = new AbortController();
      this.load();
    }
    disconnectedCallback() { this.controller?.abort(); }
    async request(url, json = false) {
      const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection request: ${response.status}`);
      return json ? response.json() : response.text();
    }
    get sourceSort() {
      // The generated all-products collection has no maintained manual order.
      // Newest-created clusters its first visible products into fewer pages.
      return /\/collections\/all\/?$/.test(this.dataset.collectionUrl) ? 'created-descending' : 'manual';
    }
    indexURL(page) {
      const url = new URL(this.dataset.collectionUrl, location.origin);
      for (const [key, value] of new URL(location.href).searchParams) {
        if (key.startsWith('filter.') || key === 'q') url.searchParams.append(key, value);
      }
      url.searchParams.set('sort_by', this.sourceSort);
      url.searchParams.set('section_id', 'euphoria-order-index');
      url.searchParams.set('page', page);
      return url;
    }
    async indexPage(page) {
      const html = await this.request(this.indexURL(page));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const data = doc.querySelector('[data-euphoria-order-index]');
      if (!data) throw new Error('Collection index unavailable');
      return JSON.parse(data.textContent);
    }
    async load() {
      try {
        const first = await this.indexPage(1);
        const rest = await pooled(Array.from({ length: Math.max(0, first.pages - 1) }, (_, i) => i + 2), p => this.indexPage(p), 6);
        const raw = [first, ...rest].flatMap(p => p.products);
        if (raw.length !== first.total || new Set(raw.map(p => p.id)).size !== raw.length) throw new Error('Collection changed while loading');
        raw.forEach((p, i) => { p.position = i; });
        const size = Number(this.dataset.size);
        const page = Number(this.dataset.page);
        const chosen = order(raw, Number(first.now)).slice((page - 1) * size, page * size);
        const grid = this.container.querySelector('#product-grid');
        const cards = new Map([...grid.children].filter(n => n.dataset.euphoriaProductId).map(n => [n.dataset.euphoriaProductId, n]));
        const columns = matchMedia('(min-width: 990px)').matches
          ? Number(grid.className.match(/grid--(\d+)-col-desktop/)?.[1] || 4)
          : Number(grid.className.match(/grid--(\d+)-col-tablet-down/)?.[1] || 2);
        const firstRow = new Set(chosen.slice(0, columns).map(p => p.id));
        this.priorityImages = [];
        const prioritize = (id, card) => {
          if (!firstRow.has(id)) return;
          const img = card.querySelector('.card__media img');
          if (!img) return;
          img.loading = 'eager';
          img.fetchPriority = 'high';
          // Start the first row while any remaining product-card pages load.
          const preload = new Image();
          preload.fetchPriority = 'high';
          preload.sizes = img.sizes;
          preload.srcset = img.srcset;
          preload.src = img.src;
          this.priorityImages.push(preload);
          firstRow.delete(id);
        };
        cards.forEach((card, id) => prioritize(id, card));
        const pages = [...new Set(chosen.filter(p => !cards.has(p.id)).map(p => Math.floor(p.position / size) + 1))];
        await pooled(pages, async sourcePage => {
          const url = new URL(location.href);
          url.searchParams.delete('section_id');
          url.searchParams.set('sections', this.dataset.section);
          url.searchParams.set('sort_by', this.sourceSort);
          url.searchParams.set('page', sourcePage);
          const sections = await this.request(url, true);
          const html = sections[this.dataset.section];
          if (!html) throw new Error('Product cards unavailable');
          // An inert template prevents fetched cards from executing scripts.
          const template = document.createElement('template');
          template.innerHTML = html;
          template.content.querySelectorAll('#product-grid > [data-euphoria-product-id]').forEach(card => {
            const id = card.dataset.euphoriaProductId;
            cards.set(id, card);
            prioritize(id, card);
          });
        });
        if (chosen.some(p => !cards.has(p.id))) throw new Error('Collection changed while loading');
        if (!this.isConnected) return;
        const nodes = chosen.map((p, i) => {
          const card = cards.get(p.id);
          card.dataset.euphoriaGroup = group(p, Number(first.now));
          card.style.setProperty('--animation-order', i + 1);
          // These elements are inserted after the theme's scroll observer ran.
          card.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
          return card;
        });
        grid.replaceChildren(...nodes);
        this.container.querySelectorAll('.pagination a[href]').forEach(link => {
          const url = new URL(link.getAttribute('href'), location.href);
          url.searchParams.delete('sort_by');
          link.href = url;
        });
        this.container.dataset.euphoriaReady = 'true';
        this.dataset.state = 'ready';
        this.replaceChildren();
        this.hidden = true;
      } catch (error) {
        if (error.name === 'AbortError' || !this.isConnected) return;
        console.error('Product ordering failed', error);
        this.dataset.state = 'error';
        const text = document.createElement('p');
        text.textContent = '商品を読み込めませんでした。もう一度お試しください。';
        const retry = document.createElement('button');
        retry.type = 'button'; retry.className = 'button'; retry.textContent = '再読み込み';
        retry.addEventListener('click', () => { retry.disabled = true; this.load(); });
        this.replaceChildren(text, retry);
      }
    }
  }
  customElements.define('euphoria-product-order', ProductOrder);
})();

}
